<script setup>
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import apiClient from "@/axiosConfig";

toastr.options = {
  positionClass: "toast-bottom-right",
  closeButton: true,
  progressBar: true,
  timeOut: 5000,
};

const callback = async (response) => {
  try {
    const credential = response.credential;
    const payload = { token: credential };
    const apiResponse = await apiClient.post("/auth/login-with-google", payload);
    document.cookie = `authToken=${apiResponse.data.token}; path=/; max-age=86400;`;
    toastr.success("Google Login successful!", "Success");
    window.location.href = "/dashboard";
  } catch (error) {
    if (error.response && error.response.status === 400) {
      toastr.error(error.response.data.message, "Login Failed");
    } else {
      toastr.error("An error occurred. Please try again later.", "Error");
    }
  }
};
</script>

<template>
  <div class="min-h-screen grid lg:grid-cols-2 bg-base">
    <!-- Brand panel -->
    <div class="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden border-r border-line">
      <div class="absolute inset-0 bg-hero-glow"></div>
      <div class="absolute inset-0 bg-grid-dark bg-grid opacity-60 [mask-image:radial-gradient(80%_80%_at_30%_30%,black,transparent)]"></div>
      <a href="/" class="relative flex items-center gap-3">
        <img src="@/assets/logo-2.png" alt="Akademi Siber" class="h-10" />
        <span class="text-lg font-extrabold text-white">Akademi<span class="text-brand">Siber</span></span>
      </a>
      <div class="relative">
        <h2 class="text-4xl font-extrabold text-white leading-tight">
          Start your<br /><span class="gradient-text">hacking journey.</span>
        </h2>
        <p class="mt-4 text-ink-muted max-w-sm">
          Free account, instant access to modules and labs. No credit card required.
        </p>
        <ul class="mt-6 space-y-3 text-sm text-ink-muted">
          <li class="flex items-center gap-3"><i class="fas fa-circle-check text-success"></i> 50+ hands-on modules</li>
          <li class="flex items-center gap-3"><i class="fas fa-circle-check text-success"></i> Real attack machines over VPN</li>
          <li class="flex items-center gap-3"><i class="fas fa-circle-check text-success"></i> 24/7 AI mentoring</li>
        </ul>
      </div>
      <div class="relative flex items-center gap-8">
        <div>
          <p class="text-[11px] text-ink-faint mb-1.5">Brought to you by</p>
          <img src="@/assets/partners/siberlab-logo.png" alt="SiberLab ID" class="h-7" />
        </div>
        <div>
          <p class="text-[11px] text-ink-faint mb-1.5">In partnership with</p>
          <img src="@/assets/partners/redac7d.png" alt="REDAC7D" class="h-7" />
        </div>
      </div>
    </div>

    <!-- Form panel -->
    <div class="flex items-center justify-center p-6 sm:p-12 overflow-y-auto">
      <div class="w-full max-w-md">
        <a href="/" class="lg:hidden flex items-center justify-center gap-2.5 mb-8">
          <img src="@/assets/logo-2.png" alt="Akademi Siber" class="h-10" />
          <span class="text-lg font-extrabold text-white">Akademi<span class="text-brand">Siber</span></span>
        </a>

        <h1 class="text-2xl font-extrabold text-white mb-1">Create an account</h1>
        <p class="text-ink-muted text-sm mb-8">Join the community and start learning today.</p>

        <form @submit.prevent="register" class="space-y-4">
          <div class="grid sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-ink-muted mb-1.5" for="username">Username</label>
              <input v-model="username" class="input" id="username" placeholder="h4ck3r" type="text" />
            </div>
            <div>
              <label class="block text-sm font-medium text-ink-muted mb-1.5" for="fullname">Full Name</label>
              <input v-model="fullname" class="input" id="fullname" placeholder="John Doe" type="text" />
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-ink-muted mb-1.5" for="email">Email</label>
            <input v-model="email" class="input" id="email" placeholder="you@example.com" type="email" />
          </div>
          <div class="grid sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-ink-muted mb-1.5" for="password">Password</label>
              <input v-model="password" class="input" id="password" placeholder="••••••••" type="password" />
            </div>
            <div>
              <label class="block text-sm font-medium text-ink-muted mb-1.5" for="confirmPassword">Confirm</label>
              <input v-model="confirmPassword" class="input" id="confirmPassword" placeholder="••••••••" type="password" />
            </div>
          </div>
          <p v-if="confirmPassword && password !== confirmPassword" class="text-xs text-brand">
            Passwords don't match.
          </p>
          <button
            :disabled="!fullname || !username || !email || !password || !confirmPassword || password !== confirmPassword"
            class="btn-primary w-full justify-center py-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            type="submit"
          >
            Create Account
          </button>
        </form>

        <div class="flex items-center gap-4 my-6">
          <div class="h-px flex-1 bg-line"></div>
          <span class="text-xs text-ink-faint uppercase tracking-wider">or</span>
          <div class="h-px flex-1 bg-line"></div>
        </div>

        <div class="flex justify-center">
          <GoogleLogin :callback="callback" />
        </div>

        <p class="text-center text-sm text-ink-muted mt-8">
          Already have an account?
          <a class="font-semibold text-brand hover:text-brand-light" href="/login">Sign in</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
toastr.options = {
  positionClass: "toast-bottom-right",
  closeButton: true,
  progressBar: true,
  timeOut: 5000,
};

export default {
  data() {
    return {
      fullname: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
    };
  },
  methods: {
    async register() {
      if (this.password !== this.confirmPassword) {
        toastr.error("Passwords don't match!", "Error");
        return;
      }
      try {
        await apiClient.post("/auth/register", {
          fullname: this.fullname,
          username: this.username,
          email: this.email,
          password: this.password,
        });
        toastr.success("Account created successfully! Please check your email for activation.", "Success");
        this.$router.push("/login");
      } catch (error) {
        if (error.response && error.response.status === 400) {
          toastr.error("Email, username, or fullname already in use. Please try again.", "Registration Failed");
        } else {
          toastr.error("An error occurred. Please try again later.", "Error");
        }
      }
    },
  },
};
</script>
